/* Guides for every module, opened from the "Guide" button in the top bar.

   Each module has:
     overview   - what the module is for, then its sections and pages (steps point at elements on the current page)
     roleIntro  - one sentence per role: what that role does in this module (shown as the second step of the overview)
     workflows  - step-by-step walk-throughs of a real job, in the order the work happens. Every step opens its page
                  (`to`) and, when it can, points at the button to press (`el`). A workflow lists the `roles` it is
                  meant for; those are shown first as "For your role". Workflows with `project: true` need a project
                  to be open (their routes contain :projectId).

   Steps whose page the user is not allowed to open are left out when the workflow starts. */
const at = (name) => `[data-tour="${name}"]`;
const tid = (name) => `[data-testid="${name}"]`;
const text = (title, description) => ({ popover: { title, description } });
const step = (name, title, description, side = "bottom") => ({ element: at(name), popover: { title, description, side } });
/** A workflow step: open `to`, point at `el` (optional), explain. `form` marks a step that lives inside the form
 *  opened by that button (the guide opens it, points at its fields, and never saves it). */
const go = (to, title, description, el, form) => ({ to, el, title, description, form });
/** The fields of one form, in order: `fields` is [title, description, element]. */
const inForm = (to, opener, fields) => fields.map(([title, description, el]) => go(to, title, description, el, opener));
const prefix = (name) => `[data-testid^="${name}"]`;      // a row button whose id ends in the document number

const SECTIONS = step("module-sections", "Sections", "This module is split into sections. Click one to see its pages on the right.");
const PAGES = step("module-pages", "Pages", "These are the pages of the section you picked. Click a page to open it.", "left");
const FIND = step("top-search", "Cannot find a page?", "Type part of its name here and press Enter. Ctrl+K also works.");

const OP = "/erp/operations/";
const DL = "/erp/delivery/";
const PI = "/erp/people-and-insight/";
const FA = "/erp/finance-and-accounting/";
const GV = "/erp/governance/";
const AP = "/erp/assets-and-plant/";
const PJ = "/projects/:projectId/";

export const ROLE_LABELS = {
  ORG_ADMIN: "Admin",
  PORTFOLIO_LEAD: "Portfolio lead",
  MEMBER: "Project team",
  ACCOUNTS: "Accounts",
  PROCUREMENT: "Procurement",
  STORES: "Stores",
  HR: "HR",
};

const ALL = ["ORG_ADMIN", "PORTFOLIO_LEAD", "MEMBER", "ACCOUNTS", "PROCUREMENT", "STORES", "HR"];

export const GUIDES = {
  /* ------------------------------------------------------------------------------------------ My Workspace */
  workspace: {
    overview: [
      text("My Workspace", "Your starting page. It shows what is waiting for you and a short summary of your work."),
      step("nav-workspace", "Come back here", "Click My Workspace in the menu whenever you want to return to this page.", "right"),
      step("top-bell", "Notifications", "New approvals and alerts show up here. A red number means something is new."),
      FIND,
    ],
    roleIntro: {
      ORG_ADMIN: "Start here each morning: approvals waiting for you and which projects need attention.",
      PORTFOLIO_LEAD: "Start here to see how every project is doing and which ones are at risk.",
      MEMBER: "Start here to see your own work for today.",
      ACCOUNTS: "Start here to see bills and payments waiting on you.",
      PROCUREMENT: "Start here to see orders and approvals waiting on you.",
      STORES: "Start here to see deliveries and issues waiting on you.",
      HR: "Start here to see attendance and payroll tasks waiting on you.",
    },
    workflows: [
      {
        id: "start-your-day", title: "Start your day", summary: "Where to look first each morning", roles: ALL,
        steps: [
          go("/workspace", "My Workspace", "Things waiting for you appear here. Open one to act on it."),
          go("/", "Portfolio Overview", "Every project at a glance: progress, cost and whether it is on track, at risk or critical."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Masters */
  masters: {
    overview: [
      text("Masters", "The lists everything else uses: companies, branches, units, tax codes and numbering. Set these up once, then they appear in dropdowns everywhere."),
      SECTIONS, PAGES,
      step("uom-add", "Add a unit", "Add a new unit of measure (bags, sqm, hours). A unit already used on a document cannot be deleted. Make it inactive instead."),
      FIND,
    ],
    roleIntro: {
      ORG_ADMIN: "You set these up once, before anyone raises a document.",
      ACCOUNTS: "You look after tax codes, bank accounts and document numbering.",
      PROCUREMENT: "You mostly pick from these lists, such as units and tax codes, when you raise orders.",
      STORES: "You mostly pick from these lists, such as units and warehouses.",
    },
    workflows: [
      {
        id: "set-up-company", title: "Set up your company", summary: "Company, branches, units, tax, bank and numbering", roles: ["ORG_ADMIN", "ACCOUNTS"],
        steps: [
          go(OP + "companies", "Company profile", "Your company's name, GSTIN, PAN, address and logo. These print on your bills and orders.", tid("create-company-button")),
          go(OP + "branches", "Branches", "Add each office or depot. Documents can be filed under a branch.", tid("create-branch-button")),
          go(OP + "units-of-measure", "Units of measure", "Bags, sqm, cum, hours. Every quantity in the system uses one of these.", tid("create-uom-button")),
          go(OP + "tax-codes", "Tax codes", "GST rates used on orders and bills.", tid("create-tax-code-button")),
          go(OP + "bank-accounts", "Bank accounts", "Every bank account and cash box you pay from or receive into. Each keeps its own ledger.", tid("create-bank-button")),
          go(OP + "numbering-series", "Numbering series", "How document numbers are built, for example PO/FY2627/0001, and when they restart.", tid("create-numbering-series-button")),
        ],
      },
      {
        id: "who-approves", title: "Decide who approves what", summary: "Job titles and approval chains", roles: ["ORG_ADMIN"],
        steps: [
          go(OP + "designations", "Designations", "Job titles. Picking one when you add a user fills in that person's starting access.", tid("add-designation-button")),
          go(OP + "approval-workflows", "Approval workflows", "Set the chain of approvers for each document type, for example a purchase order. For each step pick a role, and if you want, one named person. Then only that person is told and can approve (admins always can). The person who raised the document is never asked to approve it.", tid("create-workflow-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Client Management */
  "client-management": {
    overview: [
      text("Client Management", "From a lead to a signed contract and the bills you send the client. Flow: Lead, Proposal, Contract, then Project, then Client bills and payments."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      ORG_ADMIN: "You follow each deal from first enquiry to the project and its client bills.",
      PORTFOLIO_LEAD: "You follow each deal and turn accepted proposals into projects.",
      ACCOUNTS: "You raise the client's running bills and record the money received.",
    },
    workflows: [
      {
        id: "detail-client", detail: true, title: "Add a client, field by field", summary: "GSTIN, contact and billing defaults", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD"],
        steps: [
          go(DL + "clients", "Add a client", "The customer you build for. Press New Client.", tid("create-client-button")),
          ...inForm(DL + "clients", tid("create-client-button"), [
            ["Code", "A short unique code for the client.", tid("client-code-input")],
            ["Name", "The client's name. A name already in use is flagged.", tid("client-name-input")],
            ["GSTIN", "15 characters. A wrong format is refused, and so is a GSTIN already on another client.", tid("client-gstin-input")],
            ["Contact", "Who you deal with at the client.", tid("client-contact-input")],
            ["Billing address", "Printed on your bills to this client.", tid("client-address-input")],
            ["Default retention %", "Held back by this client on your bills. It fills in on each running bill and you can change it.", tid("client-retention-input")],
            ["Create", "Saves the client.", tid("create-client-submit-button")],
          ]),
        ],
      },
      {
        id: "detail-ra-bill", detail: true, title: "Running bill (RA), field by field", summary: "The form, the progress check, then post", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(DL + "ra-bills", "Running bill", "A running account (RA) bill charges the client for the work done so far. Press New RA Bill.", tid("create-ra-bill-button")),
          ...inForm(DL + "ra-bills", tid("create-ra-bill-button"), [
            ["Project", "The project you are billing for.", tid("ra-bill-project-select")],
            ["Client", "Who you are billing.", tid("ra-bill-client-select")],
            ["Bill date", "Required. The date of the bill.", tid("ra-bill-date-input")],
            ["Gross amount", "Suggested from the progress certified so far, and you can change it. Going above that warns you, and you must give a reason in Remarks.", tid("ra-bill-gross-amount-input")],
            ["GST %", "Tax added to the bill.", tid("ra-bill-tax-input")],
            ["Retention %", "The part the client holds back until the end.", tid("ra-bill-retention-input")],
            ["TDS %", "Tax the client deducts and pays to the government.", tid("ra-bill-tds-input")],
            ["Remarks", "Your reason, if the amount is above certified progress.", tid("ra-bill-remarks-input")],
            ["Create", "Saves the bill as a draft.", tid("create-ra-bill-submit-button")],
          ]),
          go(DL + "ra-bills", "Post", "Posting books the revenue and the amount the client owes you.", prefix("post-ra-bill-button-")),
        ],
      },
      {
        id: "detail-client-payment", detail: true, title: "Record a client receipt, field by field", summary: "The form, allocation, then post", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(DL + "client-payments", "Client receipt", "Record money received from a client and say which bills it pays. Press the new receipt button.", tid("create-client-payment-button")),
          ...inForm(DL + "client-payments", tid("create-client-payment-button"), [
            ["Client", "Who paid. Their unpaid bills then appear.", tid("client-payment-client-select")],
            ["Date", "Required. The date the money was received.", tid("client-payment-date-input")],
            ["Amount", "The amount received.", tid("client-payment-amount-input")],
            ["Allocate automatically", "Spreads the amount over the client's unpaid bills for you.", tid("client-payment-auto-allocate")],
            ["Against RA bills", "Or choose the bills and the amount for each yourself.", tid("add-client-payment-allocation-button")],
            ["Create", "Stays unavailable until the amounts are consistent. Saves the receipt as a draft.", tid("create-client-payment-submit-button")],
          ]),
          go(DL + "client-payments", "Post", "Posting records the receipt and reduces what the client owes.", prefix("post-client-payment-button-")),
        ],
      },
      {
        id: "lead-to-project", title: "From enquiry to project", summary: "Client, lead, proposal, contract, project", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD"],
        steps: [
          go(DL + "clients", "Add the client", "Create the client with its GSTIN and PAN. A wrong format is refused, and a duplicate name is flagged.", tid("create-client-button")),
          go(DL + "sales-flow", "Track the lead", "Leads move across the board from New to Won or Lost. Add notes and calls as you go."),
          go(DL + "proposals", "Prepare the proposal", "List the work with quantity and rate. Your own cost per line stays internal and is never shown to the client."),
          go(DL + "sales-flow", "Contract and project", "When the client accepts, make the contract, then create the project. Its activities and a draft estimate come from the proposal lines."),
        ],
      },
      {
        id: "bill-and-collect", title: "Bill the client and collect", summary: "Running bills, advances and receipts", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(DL + "ra-bills", "Running (RA) bills", "Bill the work done so far. You are warned if the amount is above the certified progress, and you must give a reason.", tid("create-ra-bill-button")),
          go(DL + "client-advances", "Advances", "Record any advance the client paid, so it can be set against later bills.", tid("create-client-advance-button")),
          go(DL + "client-payments", "Record a receipt", "Enter the money received and allocate it to the bills it pays.", tid("create-client-payment-button")),
          go(DL + "client-ledger", "Client ledger", "What each client still owes, bill by bill."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Projects */
  projects: {
    overview: [
      text("Projects", "Open a project, then work through its tabs from left to right: Plan (activities, estimates, schedule), Buy, Build (daily progress), Bill & Pay, and Close."),
      SECTIONS, PAGES,
      step("take-tour", "Need the full tour?", "This replays the first-time tour of the whole app."),
      FIND,
    ],
    roleIntro: {
      ORG_ADMIN: "You create projects, approve the budget and watch cost against plan.",
      PORTFOLIO_LEAD: "You watch progress, cost and risk across all your projects.",
      MEMBER: "You enter the day's progress, site expenses and equipment hours for your project.",
      ACCOUNTS: "You check costs and project finance.",
    },
    workflows: [
      {
        id: "detail-create-project", detail: true, title: "Create a project, field by field", summary: "Name, template and dates", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD"],
        steps: [
          go(DL + "all-projects", "New project", "Press New Project to open the form.", tid("create-project-button")),
          ...inForm(DL + "all-projects", tid("create-project-button"), [
            ["Project code", "The code used on this project's documents.", tid("project-code-input")],
            ["Project name", "What everyone will call it.", tid("project-name-input")],
            ["Template", "Pick a template of ready-made activities with units and costs, or start blank.", tid("project-template-select")],
            ["Location", "Where the site is.", tid("project-location-input")],
            ["Start date", "When work begins.", tid("project-start-date-input")],
            ["Target end date", "The planned finish. A project past this date is shown as at risk.", tid("project-target-end-date-input")],
            ["Create", "Creates the project and its activities from the template.", tid("create-project-submit-button")],
          ]),
        ],
      },
      {
        id: "detail-estimate", detail: true, title: "Estimate and budget, field by field", summary: "The form, then approve", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD", "MEMBER"], project: true,
        steps: [
          go(PJ + "estimates", "Estimate", "An estimate is the cost plan. Once approved it becomes the budget. Press New Estimate.", tid("create-estimate-button")),
          ...inForm(PJ + "estimates", tid("create-estimate-button"), [
            ["Remarks", "A note about this version, for example what changed.", tid("estimate-remarks-input")],
            ["Lines", "Add a line for each cost: pick the activity and the cost type (material, labour, subcontract, equipment or other), then the quantity and rate.", tid("add-estimate-line-button")],
            ["Create", "Saves it as a draft version.", tid("create-estimate-submit-button")],
          ]),
          go(PJ + "estimates", "Approve", "Approving freezes this version as the budget. Any later change is a new version. The person who last edited it may be stopped from approving it.", prefix("approve-estimate-button-")),
        ],
      },
      {
        id: "detail-daily-report", detail: true, title: "Daily report, field by field", summary: "The form, submit, approve", roles: ["MEMBER", "ORG_ADMIN", "PORTFOLIO_LEAD"], project: true,
        steps: [
          go(PJ + "daily-reports", "Daily report", "One report a day, put together from that day's progress entries, resources and delays. Press New Report.", tid("create-daily-report-button")),
          ...inForm(PJ + "daily-reports", tid("create-daily-report-button"), [
            ["Date", "Today by default. Change it to report an earlier day.", tid("daily-report-date-input")],
            ["Weather", "Optional. The day's weather.", tid("daily-report-weather-select")],
            ["Site in-charge", "Who ran the site that day.", tid("daily-report-site-incharge-input")],
            ["Remarks", "Anything worth noting.", tid("daily-report-remarks-input")],
            ["Create", "Builds the report from the day's entries.", tid("create-daily-report-submit-button")],
          ]),
          go(PJ + "daily-reports", "Submit", "Submitting locks the report and sends it for approval.", tid("submit-daily-report-button")),
          go(PJ + "daily-reports", "Approve", "The approver confirms it.", tid("approve-daily-report-button")),
        ],
      },
      {
        id: "create-project", title: "Create a project", summary: "Pick a template and start", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD"],
        steps: [
          go(DL + "setup-library", "Setup Library", "Ready-made templates of activities with units and a cost per unit. Check or edit one before you use it.", tid("create-template-button")),
          go(DL + "all-projects", "New project", "Choose the client, dates and a template. Activities are created for you.", tid("create-project-button")),
        ],
      },
      {
        id: "plan-project", title: "Plan the project", summary: "Activities, budget, schedule and people", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD", "MEMBER"], project: true,
        steps: [
          go(PJ + "measurements", "Measurements", "The overall size of the project. Keep these up to date."),
          go(PJ + "wbs", "Activities and weights", "Check each activity's quantity. An activity with no quantity cannot show progress."),
          go(PJ + "estimates", "Estimate and budget", "Create an estimate with cost lines and approve it. The approved version is your budget. The person who made it may not be allowed to approve it.", tid("create-estimate-button")),
          go(PJ + "schedule", "Master schedule", "Dates for each milestone.", tid("add-schedule-task-button")),
          go(PJ + "resources", "Resources and manpower", "Plan the people and machines you need.", tid("add-resource-button")),
        ],
      },
      {
        id: "daily-site-work", title: "Report the day's work", summary: "Progress, expenses, equipment and the daily report", roles: ["MEMBER", "ORG_ADMIN", "PORTFOLIO_LEAD"], project: true,
        steps: [
          go(PJ + "construction", "Site progress", "Enter today's output for each activity. Progress and the S-curve update from this."),
          go(PJ + "site-expenses", "Site expenses", "Diesel, food, transport and wages. One entry updates the project cost and the accounts together.", tid("add-site-expense-button")),
          go(PJ + "logbook", "Equipment logbook", "Hours and fuel for each machine. This also books the machine's cost to the activity.", tid("create-logbook-entry-button")),
          go(PJ + "daily-reports", "Daily report", "One report a day, put together from the day's entries. Submit it for approval; it locks after that.", tid("create-daily-report-button")),
          go(PJ + "issues", "Issues and risks", "Log anything blocking the work so it is followed to closure."),
        ],
      },
      {
        id: "cost-control", title: "Check cost against plan", summary: "Plan vs actual, costs, finance and close-out", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD", "ACCOUNTS"], project: true,
        steps: [
          go(PJ + "plan-vs-actual", "Plan vs actual", "Planned quantities against what was actually done each day or week."),
          go(PJ + "estimates", "Budget vs actual by cost type", "For each activity: planned and actual quantity and amount for material, labour, subcontract and equipment."),
          go(PJ + "cost-entries", "Cost entries", "Every cost booked to the project, from bills, issues and site expenses.", tid("create-cost-entry-button")),
          go(PJ + "finance", "Project finance", "Budget, committed, actual cost, margin, cash and forecast in one place."),
          go(PJ + "closeout", "Close the project", "Clear the checklist, move leftover stock to other sites, then close. A closed project takes no new orders."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Procurement */
  procurement: {
    overview: [
      text("Procurement", "Buying material and giving work out. Flow: Indent (ask), Requisition, RFQ (quotes), Purchase Order, Goods Receipt, Vendor Bill, Payment. For subcontract work: Service Requisition, Work Order, Measurement (JMR), Service Bill."),
      SECTIONS, PAGES,
      step("indent-new", "New indent", "Ask for material for a site. This is where buying starts."),
      step("grn-new", "New goods receipt", "Record what was delivered. You cannot receive more than was ordered."),
      step("wo-new", "New work order", "Give work to a subcontractor with quantities and rates."),
      step("bill-new", "New purchase bill", "Enter the vendor's bill. Add the vendor's bill number before posting."),
      step("service-bill-new", "New service bill", "Bill a subcontractor for work measured and certified."),
      FIND,
    ],
    roleIntro: {
      ORG_ADMIN: "You approve orders and watch where each purchase is waiting.",
      PROCUREMENT: "You raise indents, get quotes, place orders and give out subcontract work.",
      STORES: "You receive the goods that were ordered and record what arrived.",
      ACCOUNTS: "You post vendor bills, raise debit notes and pay vendors.",
      MEMBER: "You raise indents and requisitions for your site and measure subcontract work.",
      PORTFOLIO_LEAD: "You follow what is being bought and where it is stuck.",
    },
    workflows: [
      {
        id: "detail-item", detail: true, title: "Add an item, field by field", summary: "Name, code and price per unit", roles: ["STORES", "PROCUREMENT", "ORG_ADMIN"],
        steps: [
          go(OP + "items-and-prices", "Add an item", "Your catalogue of materials and services. Every order form offers these in a dropdown. Press New Item.", tid("create-item-button")),
          ...inForm(OP + "items-and-prices", tid("create-item-button"), [
            ["Code", "A short unique code.", tid("item-code-input")],
            ["Name", "What the item is called on orders and bills.", tid("item-name-input")],
            ["Price for 1 unit", "The price of one unit before GST. Order forms fill their rate from this and you can change it on the order.", tid("item-rate-input")],
          ]),
        ],
      },
      {
        id: "detail-indent", detail: true, title: "Raise an indent, field by field", summary: "The form, then submit and convert", roles: ["PROCUREMENT", "MEMBER", "ORG_ADMIN"],
        steps: [
          go(OP + "purchase-indents", "Raise an indent", "An indent is the site's request for material, and it starts the buying. Press New Indent to open the form.", tid("create-indent-button")),
          ...inForm(OP + "purchase-indents", tid("create-indent-button"), [
            ["Project", "Pick the project the material is for. Its cost and approvals follow this project.", tid("indent-project-select")],
            ["Date", "Required. Pick the date of the request.", tid("indent-date-input")],
            ["Items", "Add one line for each material. Pick it from your catalogue, enter the quantity and an estimated rate. The rate fills in from Items and Prices and you can change it.", tid("add-indent-item-button")],
            ["Create", "This saves the indent as a draft. Nothing is ordered yet, and you can still edit it.", tid("create-indent-submit-button")],
          ]),
          go(OP + "purchase-indents", "Submit for approval", "On a draft indent, Submit sends it to the approver. Its status in the list changes.", prefix("submit-indent-button-")),
          go(OP + "purchase-indents", "Convert", "Once approved, Convert turns the indent into a purchase requisition, the next step in buying.", prefix("convert-indent-button-")),
        ],
      },
      {
        id: "detail-rfq", detail: true, title: "Get quotes (RFQ), field by field", summary: "Create, invite vendors, enter quotes, convert to PO", roles: ["PROCUREMENT", "ORG_ADMIN"],
        steps: [
          go(OP + "rfqs", "Request for quotation", "An RFQ asks several vendors to quote for the same items, so you can compare. Press New RFQ.", tid("create-rfq-button")),
          ...inForm(OP + "rfqs", tid("create-rfq-button"), [
            ["Project", "Pick the project the material is for.", tid("rfqs-project-select")],
            ["Date", "Required. The date you are sending the request.", tid("rfq-date-input")],
            ["Items", "Add each item and the quantity you want quoted.", tid("add-rfq-item-button")],
            ["Create", "Saves the RFQ as a draft. You invite vendors next.", tid("create-rfq-submit-button")],
          ]),
          go(OP + "rfqs", "Invite vendors", "Choose which vendors to ask. A vendor needs a phone number or email to receive the request.", prefix("invite-vendor-button-")),
          go(OP + "rfqs", "Enter the quotes", "For each vendor, enter the rate quoted for each item. Put delivery time and terms in Remarks.", prefix("rfq-vendor-quote-button-")),
          go(OP + "rfqs", "Select the best", "Pick the vendor whose quote you accept.", prefix("select-rfq-vendor-button-")),
          go(OP + "rfqs", "Convert to PO", "Turns the selected quote into a purchase order for that vendor, with the quoted rates.", prefix("convert-rfq-button-")),
        ],
      },
      {
        id: "detail-po", detail: true, title: "Create a purchase order, field by field", summary: "The form, then submit and approve", roles: ["PROCUREMENT", "ORG_ADMIN"],
        steps: [
          go(OP + "purchase-orders", "Purchase order", "A purchase order is your commitment to a vendor. Press New PO.", tid("create-po-button")),
          ...inForm(OP + "purchase-orders", tid("create-po-button"), [
            ["Copy from", "Optional. Start from an RFQ or a requisition and the vendor and items are filled in for you.", tid("po-source-select")],
            ["Project", "The project the order is for.", tid("po-project-select")],
            ["Vendor", "Who you are buying from.", tid("po-vendor-select")],
            ["Date", "Required. The order date.", tid("po-date-input")],
            ["GST", "The tax code to apply to the order. Leave it at No tax for none.", tid("po-tax-select")],
            ["Items", "Add each item with its quantity and rate.", tid("add-po-item-button")],
            ["Total", "The order total, worked out from the lines and the tax.", tid("po-total")],
            ["Create", "Saves the order as a draft.", tid("create-po-submit-button")],
          ]),
          go(OP + "purchase-orders", "Submit", "On a draft order, Submit sends it for approval.", prefix("po-submit-")),
          go(OP + "purchase-orders", "Approve", "An approver approves it. Only an approved order can be received. Your organisation may stop people approving what they submitted themselves.", prefix("po-approve-")),
        ],
      },
      {
        id: "detail-grn", detail: true, title: "Receive goods (GRN), field by field", summary: "The form, then post", roles: ["STORES", "PROCUREMENT", "ORG_ADMIN"],
        steps: [
          go(OP + "goods-receipt-notes", "Goods receipt", "A goods receipt records what actually arrived against an approved order. Press New GRN.", tid("create-grn-button")),
          ...inForm(OP + "goods-receipt-notes", tid("create-grn-button"), [
            ["Purchase order", "Pick the approved order the goods came against.", tid("grn-po-select")],
            ["Date", "Required. The date the goods arrived.", tid("grn-date-input")],
            ["Where it goes", "Pick the warehouse that will hold it, and it becomes stock. Or choose straight to site if it goes directly to the job.", tid("grn-warehouse-select")],
            ["Quantities received", "After you pick the order, its lines appear here. Enter what arrived on each line. It starts at what is still pending and cannot go past what was ordered.", tid("grn-po-items")],
            ["Create", "Saves the receipt as a draft.", tid("create-grn-submit-button")],
          ]),
          go(OP + "goods-receipt-notes", "Post", "Posting makes it official: the stock goes up, and a vendor bill is drafted for you from it.", prefix("post-grn-button-")),
        ],
      },
      {
        id: "detail-purchase-bill", detail: true, title: "Vendor bill, field by field", summary: "The form, the three-way check, then post", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(OP + "purchase-bills", "Vendor bill", "A bill is usually drafted for you when a goods receipt is posted. You can also enter one yourself. Press New Purchase Bill.", tid("create-bill-button")),
          ...inForm(OP + "purchase-bills", tid("create-bill-button"), [
            ["Vendor", "Who sent the bill.", tid("bill-vendor-select")],
            ["Against GRN", "Pick the goods receipt you are billing. This switches on the three-way check of bill, receipt and order.", tid("bill-grn-select")],
            ["Vendor bill no.", "Required before posting. Copy it from the vendor's invoice.", tid("bill-vendor-bill-no-input")],
            ["Bill date", "The date on the vendor's invoice.", tid("bill-date-input")],
            ["Quantity and rate", "After you pick a goods receipt, its lines appear here with what was received. Enter the quantity and rate from the invoice. A quantity or rate outside the order is refused.", tid("bill-grn-items")],
            ["Override", "If the invoice differs from the order and the difference is agreed with the vendor, an override option lets you go ahead. It needs a reason.", tid("bill-match-override-checkbox")],
            ["Create", "Saves the bill as a draft.", tid("create-bill-submit-button")],
          ]),
          go(OP + "purchase-bills", "Post", "Posting adds the bill to what you owe the vendor. It will not post without the vendor's bill number.", prefix("post-bill-button-")),
        ],
      },
      {
        id: "detail-vendor-payment", detail: true, title: "Pay a vendor, field by field", summary: "The form, allocation, then post", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(OP + "vendor-payments", "Pay a vendor", "Record money paid and say which bills it settles. Press New Payment.", tid("create-payment-button")),
          ...inForm(OP + "vendor-payments", tid("create-payment-button"), [
            ["Vendor", "Who you are paying. Their unpaid bills then appear.", tid("payment-vendor-select")],
            ["Date", "Required. The payment date.", tid("payment-date-input")],
            ["Amount", "The amount paid.", tid("payment-amount-input")],
            ["Pay all pending", "Once you pick a vendor with unpaid bills, this fills the amount and spreads it over those bills, oldest first.", tid("pay-all-pending-button")],
            ["Bills to pay", "Or add the bills one by one and enter the amount for each. This appears after you pick a vendor.", tid("add-payment-allocation-button")],
            ["Create", "Stays unavailable until the amounts add up. Saves the payment as a draft.", tid("create-payment-submit-button")],
          ]),
          go(OP + "vendor-payments", "Post", "Posting records the payment and reduces what you owe.", prefix("post-payment-button-")),
        ],
      },
      {
        id: "detail-service-requisition", detail: true, title: "Service requisition, field by field", summary: "The form, submit, approve", roles: ["PROCUREMENT", "MEMBER", "ORG_ADMIN"],
        steps: [
          go(OP + "service-requisitions", "Service requisition", "Describes work you want a subcontractor to do, before a work order is raised. Press New Service Requisition.", tid("create-sr-button")),
          ...inForm(OP + "service-requisitions", tid("create-sr-button"), [
            ["Project", "The project the work is for.", tid("sr-project-select")],
            ["Date", "Required. The date of the request.", tid("sr-date-input")],
            ["Done by", "Contractor if a subcontractor will do it, or your own company if you will.", tid("sr-done-by-contractor")],
            ["Preferred vendor", "The subcontractor you want. When the requisition is approved, a work order is drafted for them.", tid("sr-preferred-vendor-select")],
            ["Scope of work", "Describe the work in plain words.", tid("sr-scope-input")],
            ["Items", "Add each activity with the quantity and unit of work.", tid("add-sr-item-button")],
            ["Create", "Saves it as a draft.", tid("create-sr-submit-button")],
          ]),
          go(OP + "service-requisitions", "Submit", "Sends the draft for approval.", prefix("sr-submit-button-")),
          go(OP + "service-requisitions", "Approve", "Once approved, a draft work order is made for the preferred vendor with your lines.", prefix("sr-approve-button-")),
        ],
      },
      {
        id: "detail-work-order", detail: true, title: "Work order, field by field", summary: "The form, submit, approve", roles: ["PROCUREMENT", "ORG_ADMIN"],
        steps: [
          go(OP + "work-orders", "Work order", "Your agreement with the subcontractor: what, how much and at what rate. Press New Work Order.", tid("create-wo-button")),
          ...inForm(OP + "work-orders", tid("create-wo-button"), [
            ["Copy from requisition", "Optional. Start from an approved service requisition and its project and lines come across.", tid("wo-source-sr-select")],
            ["Project", "The project the work is for.", tid("wo-project-select")],
            ["Vendor", "The subcontractor.", tid("wo-vendor-select")],
            ["Date", "Required. The work order date.", tid("wo-date-input")],
            ["Site location", "Where the work is done.", tid("wo-site-input")],
            ["Tax %", "GST on the work, for example 18.", tid("wo-tax-input")],
            ["Items", "Add each line with its quantity and rate.", tid("add-wo-item-button")],
            ["Create", "Saves it as a draft.", tid("create-wo-submit-button")],
          ]),
          go(OP + "work-orders", "Submit", "Sends the draft for approval.", prefix("submit-wo-button-")),
          go(OP + "work-orders", "Approve", "Approving drafts a measurement (JMR) for every line, ready for you to fill in.", prefix("approve-wo-button-")),
        ],
      },
      {
        id: "detail-jmr", detail: true, title: "Measure the work (JMR), field by field", summary: "The form, then certify", roles: ["MEMBER", "PROCUREMENT", "ORG_ADMIN"],
        steps: [
          go(OP + "contractor-jmrs", "Joint measurement", "A JMR records the quantity of work actually done on a work order line. Press New JMR.", tid("create-jmr-button")),
          ...inForm(OP + "contractor-jmrs", tid("create-jmr-button"), [
            ["Work order line", "Pick the approved work order line you measured.", tid("jmr-wo-item-select")],
            ["Date", "Required. The date of the measurement.", tid("jmr-date-input")],
            ["Measured qty", "The quantity measured this time. The total measured on the line cannot go more than 10% over what was ordered.", tid("jmr-qty-input")],
            ["Remarks", "An optional note, for example the part of the site measured.", tid("jmr-remarks-input")],
            ["Create", "Saves it as a draft. You can still edit or delete a draft.", tid("create-jmr-submit-button")],
          ]),
          go(OP + "contractor-jmrs", "Certify", "Certifying confirms the measurement. It adds to the activity's progress and drafts the subcontractor's bill.", prefix("certify-jmr-button-")),
        ],
      },
      {
        id: "detail-service-bill", detail: true, title: "Subcontractor bill, field by field", summary: "The form, retention and TDS, then post", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(OP + "service-bills", "Subcontractor bill", "Usually drafted when you certify a measurement, and you can also enter one. Press New Service Bill.", tid("create-service-bill-button")),
          ...inForm(OP + "service-bills", tid("create-service-bill-button"), [
            ["Work order", "Pick the approved work order. The certified and already-billed quantities are shown.", tid("service-bill-wo-select")],
            ["Vendor bill no.", "The subcontractor's invoice number.", tid("service-bill-vendor-bill-no-input")],
            ["Bill date", "Required. The date on the invoice.", tid("service-bill-date-input")],
            ["Billed quantities", "After you pick the work order, its lines appear here. Enter the quantity to bill on each line. You can bill only what has been certified.", tid("service-bill-wo-items")],
            ["Retention %", "The part of the bill you hold back until the end of the job.", tid("service-bill-retention-input")],
            ["Tax %", "GST on the bill.", tid("service-bill-tax-input")],
            ["TDS %", "Tax deducted at source. You keep it back and pay it to the government.", tid("service-bill-tds-input")],
            ["Create", "Saves the bill as a draft.", tid("create-service-bill-submit-button")],
          ]),
          go(OP + "service-bills", "Post", "Posting adds it to what you owe the subcontractor, less retention and TDS.", prefix("post-service-bill-button-")),
        ],
      },
      {
        id: "detail-vendor", detail: true, title: "Add a vendor, field by field", summary: "Codes, GSTIN, PAN and contact", roles: ["PROCUREMENT", "ORG_ADMIN"],
        steps: [
          go(OP + "vendors", "Add a vendor", "Suppliers and subcontractors you buy from. Press Add New Vendor.", tid("create-vendor-button")),
          ...inForm(OP + "vendors", tid("create-vendor-button"), [
            ["Code", "A short unique code for the vendor.", tid("vendor-code-input")],
            ["Name", "The vendor's name. A name already in use is flagged.", tid("vendor-name-input")],
            ["GSTIN", "15 characters. A wrong format is refused, and so is a GSTIN already on another vendor.", tid("vendor-gstin-input")],
            ["PAN", "10 characters in the form AAAAA9999A.", tid("vendor-pan-input")],
            ["Phone", "Needed to send the vendor RFQ requests.", tid("vendor-phone-input")],
            ["Email", "Also used to send RFQ requests.", tid("vendor-email-input")],
            ["Default retention %", "Filled onto this vendor's subcontract bills. You can change it on each bill.", tid("vendor-retention-input")],
            ["Create", "Saves the vendor.", tid("create-vendor-submit-button")],
          ]),
        ],
      },
      {
        id: "buy-material", title: "Buy material", summary: "Indent to payment", roles: ["PROCUREMENT", "MEMBER", "ORG_ADMIN"],
        steps: [
          go(OP + "purchase-indents", "Indent", "The site asks for material: item, quantity and project. This starts the buying.", tid("create-indent-button")),
          go(OP + "purchase-requisitions", "Requisition", "The reviewed request. It can be made from an approved indent.", tid("create-requisition-button")),
          go(OP + "rfqs", "Get quotes (RFQ)", "Invite vendors, enter their quotes, select the best one, then Convert to PO.", tid("create-rfq-button")),
          go(OP + "purchase-orders", "Purchase order", "Submit the order for approval. Once approved, it can be received.", tid("create-po-button")),
          go(OP + "goods-receipt-notes", "Receive the goods", "Record what arrived and Post it. Pick a warehouse to hold it as stock, or leave it empty if it goes straight to site.", tid("create-grn-button")),
          go(OP + "purchase-bills", "Vendor bill", "A bill is drafted from the receipt. Add the vendor's bill number and post it. Quantity and rate are checked against the order and receipt.", tid("create-bill-button")),
          go(OP + "vendor-payments", "Pay the vendor", "Pay and allocate the payment to the bills it settles.", tid("create-payment-button")),
        ],
      },
      {
        id: "subcontract", title: "Give work to a subcontractor", summary: "Requisition, work order, measurement, bill", roles: ["PROCUREMENT", "MEMBER", "ORG_ADMIN"],
        steps: [
          go(OP + "service-requisitions", "Service requisition", "Describe the work and pick the preferred vendor. When it is approved, a work order is drafted for that vendor.", tid("create-sr-button")),
          go(OP + "work-orders", "Work order", "Check the rates and approve it. A measurement (JMR) is drafted for each line.", tid("create-wo-button")),
          go(OP + "contractor-jmrs", "Measure the work (JMR)", "Enter the quantity measured. It cannot go more than 10% over what was ordered. Then Certify it.", tid("create-jmr-button")),
          go(OP + "service-bills", "Service bill", "Drafted when you certify. Post it; retention and TDS are taken off.", tid("create-service-bill-button")),
          go(OP + "vendor-payments", "Pay the subcontractor", "Pay and allocate to the service bill.", tid("create-payment-button")),
        ],
      },
      {
        id: "receive-goods", title: "Receive goods", summary: "What the store does on delivery", roles: ["STORES"],
        steps: [
          go(OP + "goods-receipt-notes", "Goods receipt", "Pick the order, enter the quantity that arrived and Post. You cannot receive more than was ordered.", tid("create-grn-button")),
          go(OP + "stock-and-balances", "Check the stock", "The received quantity now shows in the warehouse you chose."),
          go(OP + "goods-issues", "Issue to the job", "When the site needs it, issue it to the project and activity.", tid("create-gin-button")),
        ],
      },
      {
        id: "bills-and-payments", title: "Bills and payments", summary: "Post bills, fix differences, pay vendors", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(OP + "purchase-bills", "Purchase bills", "Check each drafted bill against the order and receipt, add the vendor's bill number, and post.", tid("create-bill-button")),
          go(OP + "service-bills", "Service bills", "Post subcontractor bills from certified measurements."),
          go(OP + "debit-notes", "Debit notes", "Reduce what you owe for returns, price or quantity differences, quality and penalties. One is drafted for you when a bill is over the order.", tid("create-debit-note-button")),
          go(OP + "vendor-advances", "Advances", "Money paid to a vendor before the bill.", tid("create-advance-button")),
          go(OP + "vendor-payments", "Pay vendors", "Pick a vendor and allocate the payment to its oldest bills first.", tid("create-payment-button")),
          go(OP + "vendor-ledger", "Vendor ledger", "What you owe each vendor, and how old it is."),
        ],
      },
      {
        id: "track-buying", title: "Track the buying", summary: "Pipeline, tracker, vendors and prices", roles: ["PROCUREMENT", "PORTFOLIO_LEAD", "ORG_ADMIN"],
        steps: [
          go(OP + "pipeline", "Pipeline", "Everything being bought, by the stage it is waiting at. Red cards have waited longer than normal. Click one to open it."),
          go(OP + "procurement-tracker", "Tracker", "Each item per project: ordered, received, billed and paid."),
          go(OP + "vendors", "Vendors", "Add vendors with a valid GSTIN and PAN. A duplicate name is flagged.", tid("create-vendor-button")),
          go(OP + "items-and-prices", "Items and prices", "Your catalogue with the price per unit. Order forms fill the price from here.", tid("create-item-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Inventory */
  inventory: {
    overview: [
      text("Inventory", "Stock in your stores. Material comes in from goods receipts, goes out as issues to a job, and can be returned or moved between stores."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      STORES: "You keep the stock right: receive, issue to jobs, take returns and correct counts.",
      PROCUREMENT: "You check what is in the stores before ordering more.",
      MEMBER: "You ask for material from the store and return what is unused.",
      ORG_ADMIN: "You set up the stores and check stock and its value.",
    },
    workflows: [
      {
        id: "detail-goods-issue", detail: true, title: "Issue material to a job, field by field", summary: "The form, then post", roles: ["STORES", "ORG_ADMIN"],
        steps: [
          go(OP + "goods-issues", "Goods issue", "Moves material from the store to a job. Press New Goods Issue.", tid("create-gin-button")),
          ...inForm(OP + "goods-issues", tid("create-gin-button"), [
            ["Warehouse", "The store the material comes out of.", tid("gin-warehouse-select")],
            ["Project", "The job it goes to.", tid("gin-project-select")],
            ["Activity", "The activity the material is used on. Its cost is booked to this activity.", tid("gin-wbs-select")],
            ["Date", "Required. The date of issue.", tid("gin-date-input")],
            ["Items", "Add each item and quantity. The stock available is shown, and you cannot issue more than is there.", tid("add-gin-item-button")],
            ["Create", "Saves it as a draft.", tid("create-gin-submit-button")],
          ]),
          go(OP + "goods-issues", "Post", "Posting takes the stock out and books its cost to the job at the store's average cost.", prefix("post-gin-button-")),
        ],
      },
      {
        id: "set-up-store", title: "Set up a store", summary: "Warehouses and items", roles: ["STORES", "ORG_ADMIN"],
        steps: [
          go(OP + "warehouses", "Warehouses", "A site store or depot. Stock is tracked per item per warehouse.", tid("create-warehouse-button")),
          go(OP + "items-and-prices", "Items", "The catalogue of materials. Each item has a unit and a price.", tid("create-item-button")),
          go(OP + "receipts", "Opening stock", "Use a manual receipt for stock you already have, or material that did not come on an order.", tid("create-receipt-button")),
        ],
      },
      {
        id: "stock-movements", title: "Receive, issue and return", summary: "Day-to-day stock work", roles: ["STORES", "MEMBER", "ORG_ADMIN"],
        steps: [
          go(OP + "stock-and-balances", "Stock and balances", "What is on hand now and its average cost, per item and warehouse."),
          go(OP + "goods-issues", "Issue to a job", "Pick the project and the activity. This is when the cost reaches the job, at the store's average cost.", tid("create-gin-button")),
          go(OP + "store-returns", "Returns from site", "Unused material coming back, at the cost it was issued at.", tid("create-srn-button")),
          go(OP + "transfers", "Transfers", "Move stock between warehouses of the same company.", tid("create-transfer-button")),
          go(OP + "adjustments", "Stock adjustments", "Correct the stock after a physical count. Raise it (IN) or reduce it (OUT).", tid("create-adjustment-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Production */
  production: {
    overview: [
      text("Production", "Plants, bills of material, production runs and dispatches, for work you make yourself (for example concrete or blocks)."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      MEMBER: "You run production and record what leaves the plant.",
      STORES: "You keep the plant store's stock right.",
      ORG_ADMIN: "You set up plants and recipes and check production cost.",
    },
    workflows: [
      {
        id: "make-and-dispatch", title: "Make and dispatch", summary: "Plant, recipe, run, dispatch", roles: ["MEMBER", "STORES", "ORG_ADMIN"],
        steps: [
          go(DL + "plants-and-bom", "Plants and recipes", "Add a plant (crusher, batching, hot-mix) and the recipe (BOM) of what goes into each product.", tid("create-plant-button")),
          go(DL + "production-runs", "Production run", "Post a run to use the recipe's materials from stock, receive the finished goods and book the cost.", tid("create-production-run-button")),
          go(DL + "dispatches", "Dispatch", "Send finished goods to your own project (at production cost) or sell them to a client.", tid("create-dispatch-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Assets & Plant */
  "assets-and-plant": {
    overview: [
      text("Assets & Plant", "Your machines and equipment: the register, hire orders, daily running logs and depreciation."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      MEMBER: "You log the hours your machines work each day.",
      ACCOUNTS: "You keep the asset register and run depreciation.",
      ORG_ADMIN: "You see what you own and what it costs to run.",
    },
    workflows: [
      {
        id: "machines", title: "Machines and hired equipment", summary: "Equipment, hire orders and weighbridge", roles: ["MEMBER", "ORG_ADMIN"],
        steps: [
          go(AP + "equipment", "Equipment", "Add each machine with its hourly rate. The rate drives the cost when hours are logged.", tid("create-equipment-button")),
          go(AP + "hire-orders", "Hire orders", "For hired machines: the vendor, the rate and the project it works at.", tid("create-hire-order-button")),
          go(AP + "weighbridge", "Weighbridge", "Gross and tare tickets for incoming trucks. Net weight must be positive.", tid("create-weighbridge-ticket-button")),
        ],
      },
      {
        id: "fixed-assets", title: "Fixed assets and depreciation", summary: "Register and monthly depreciation", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(AP + "asset-register", "Asset register", "Cost, depreciation so far and book value. A draft asset from a bill needs a method and confirmation first.", tid("create-asset-button")),
          go(AP + "depreciation-runs", "Depreciation run", "One run per company per period. Preview as often as you like; posting is safe to repeat.", tid("post-depreciation-run-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ HR & Payroll */
  "hr-and-payroll": {
    overview: [
      text("HR & Payroll", "Employees, daily attendance and the monthly payroll run. Attendance feeds the pay."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      HR: "You keep employees and attendance right, then run the month's payroll.",
      ACCOUNTS: "You post the payroll to the accounts and pay it from the bank.",
      ORG_ADMIN: "You oversee salaries and payroll.",
    },
    workflows: [
      {
        id: "detail-payroll", detail: true, title: "Payroll run, field by field", summary: "Prepare the month, post, pay", roles: ["HR", "ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(PI + "payroll-runs", "Payroll run", "One run a month for every active employee. Press New Payroll Run.", tid("new-payroll-run-button")),
          ...inForm(PI + "payroll-runs", tid("new-payroll-run-button"), [
            ["Month", "The month to pay. Each employee is paid for the days marked in attendance. An employee with nothing marked is paid the full month.", tid("payroll-month-input")],
            ["Prepare", "Works out every salary and saves the run as a draft for you to check.", tid("payroll-prepare-button")],
          ]),
          go(PI + "payroll-runs", "Post", "Open a run, check the lines, then Post. This puts the salaries into the accounts.", tid("post-payroll-button")),
          go(PI + "payroll-runs", "Bank file", "Download the file to pay the salaries through the bank.", tid("bank-file-button")),
        ],
      },
      {
        id: "monthly-payroll", title: "Run the monthly payroll", summary: "Employees, attendance, run, post, pay", roles: ["HR", "ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(PI + "employees", "Employees", "Salary details used by payroll. Site staff linked to a project have their salary booked as that project's labour cost.", tid("add-employee-button")),
          go(PI + "attendance", "Attendance", "Mark attendance day by day, or mark today for the whole team. An employee with nothing marked is paid the full month."),
          go(PI + "payroll-runs", "Payroll run", "One run per month. Prepare it, check the lines, post it to the accounts, then pay through the bank and record PF, ESI and TDS.", tid("new-payroll-run-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Finance */
  finance: {
    overview: [
      text("Finance & Accounting", "The books: chart of accounts, journals, the ledger and closing a period. Bills and payments you post elsewhere appear here automatically."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      ACCOUNTS: "You keep the books: journals, the ledger, and closing each month.",
      ORG_ADMIN: "You check the books and approve the period close.",
    },
    workflows: [
      {
        id: "detail-journal", detail: true, title: "Post a journal, field by field", summary: "Date, narration, lines that balance", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(FA + "post-journal", "Manual journal", "A manual double-entry voucher, for anything the system does not post by itself. Press New Journal Entry.", tid("create-journal-button")),
          ...inForm(FA + "post-journal", tid("create-journal-button"), [
            ["Date", "Required. The date of the entry. It must fall in an open period.", tid("journal-date-input")],
            ["Narration", "Say what the entry is for.", tid("journal-narration-input")],
            ["Lines", "Add a line for each account, with a debit or a credit.", tid("add-journal-line-button")],
            ["Balance", "Debits must equal credits. Posting stays unavailable until they do.", tid("journal-balance-indicator")],
            ["Post", "Posts the entry to the books.", tid("create-journal-submit-button")],
          ]),
        ],
      },
      {
        id: "books-and-close", title: "Keep the books and close a period", summary: "Accounts, journals, ledger, close", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(FA + "chart-of-accounts", "Chart of accounts", "Groups collect the accounts under them. Journals post only to ordinary accounts.", tid("create-account-button")),
          go(FA + "post-journal", "Post a journal", "A manual entry. Debits must equal credits before it will post.", tid("create-journal-button")),
          go(FA + "ledger-and-tally-export", "Ledger and Tally export", "Open any account's running balance, or export all vouchers for Tally."),
          go(FA + "periods-and-year-end", "Close the period", "Closing a month blocks any further posting into it, so do it only when the books are final."),
          go(FA + "inter-company-loans", "Inter-company loans", "Posts matching entries in the lender's and the borrower's books.", tid("create-loan-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Governance */
  governance: {
    overview: [
      text("Governance & Statutory", "Tax (TDS) filings, legal agreements, cases and notices, and licences, with their due dates."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      ACCOUNTS: "You handle the statutory deposits such as TDS.",
      ORG_ADMIN: "You keep agreements, licences and legal matters on record with their dates.",
    },
    workflows: [
      {
        id: "statutory-and-legal", title: "Statutory and legal records", summary: "TDS, agreements, licences, cases", roles: ["ACCOUNTS", "ORG_ADMIN"],
        steps: [
          go(PI + "statutory", "Statutory (TDS)", "TDS deducted and deposited. This is handled by the Accounts team."),
          go(GV + "agreements", "Agreements", "Title, parties, tenure, renewal and value of each agreement you have signed.", tid("create-agreement-button")),
          go(GV + "statutory-approvals", "Statutory approvals", "Licences and permits with their expiry dates.", tid("create-statutory-approval-button")),
          go(GV + "cases-and-notices", "Cases and notices", "Case number, forum, counsel and the next hearing date.", tid("create-case-button")),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Reports */
  reports: {
    overview: [
      text("Reports & MIS", "Summary reports: the executive dashboard, ageing of bills, cost to completion and financial statements."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      ORG_ADMIN: "You use these to run the weekly review.",
      PORTFOLIO_LEAD: "You use these to compare projects and spot the ones in trouble.",
      ACCOUNTS: "You use these for ageing of bills and the financial statements.",
    },
    workflows: [
      {
        id: "weekly-review", title: "Weekly review", summary: "Which reports to open, in order", roles: ["ORG_ADMIN", "PORTFOLIO_LEAD", "ACCOUNTS"],
        steps: [
          go(PI + "executive-dashboard", "Executive dashboard", "Revenue, cost and margin across all projects, and what is overdue."),
          go(PI + "cost-to-completion", "Cost to completion", "For each activity: what is spent, what is committed and what is left to spend."),
          go(PI + "aging-reports", "Ageing", "Bills and receipts by how long they have been outstanding."),
          go(PI + "finance-summary", "Finance summary", "Cash, receivables and payables at a glance."),
          go(PI + "financial-statements", "Financial statements", "Profit and loss and balance sheet."),
          go(PI + "operations-reports", "Operations reports", "Manpower and machine use, and other site reports."),
        ],
      },
    ],
  },

  /* ------------------------------------------------------------------------------------------ Administration */
  administration: {
    overview: [
      text("Administration", "Who can use BuildNexa and what they can see: users, roles and access, the audit log and organisation settings."),
      SECTIONS, PAGES, FIND,
    ],
    roleIntro: {
      ORG_ADMIN: "You decide who can log in, what each role can do, and the rules the system enforces.",
    },
    workflows: [
      {
        id: "detail-user", detail: true, title: "Add a user, field by field", summary: "Login, role and starting access", roles: ["ORG_ADMIN"],
        steps: [
          go(GV + "users", "Add a user", "Press Add User to create a login.", tid("add-user-button")),
          ...inForm(GV + "users", tid("add-user-button"), [
            ["Full name", "The person's name as it should appear on approvals.", tid("user-full-name-input")],
            ["Email", "Their login. It must be unique in your organisation.", tid("user-email-input")],
            ["Phone", "Optional. Their mobile number.", tid("user-phone-input")],
            ["Designation", "A job title. Picking one fills in a starting role and project role, and you can still change both.", tid("user-designation-select")],
            ["Role", "What the person can do in each module follows the Permission Matrix for this role.", tid("user-role-select")],
            ["Password", "Their first password. Ask them to change it.", tid("user-password-input")],
            ["Save", "Creates the login.", tid("user-form-save-button")],
          ]),
        ],
      },
      {
        id: "add-people", title: "Add people and control access", summary: "Users, roles and what each can see", roles: ["ORG_ADMIN"],
        steps: [
          go(GV + "users", "Add a user", "Create a login and give a role. Module access follows the Permission Matrix for that role.", tid("add-user-button")),
          go(GV + "access", "Access", "Pick a role or a person and tick what they can do in each module: View, Create, Edit, Delete, Approve, Post."),
          go(GV + "org-settings", "Organisation settings", "Fiscal year, matching tolerances, stock policy and whether people may approve their own documents."),
        ],
      },
      {
        id: "keep-it-healthy", title: "Keep the system healthy", summary: "Audit log, integrity checks, backups", roles: ["ORG_ADMIN"],
        steps: [
          go(GV + "audit-log", "Audit log", "Who did what to which document, and when: postings, approvals and unlocks."),
          go(GV + "integrity-checks", "Integrity checks", "Checks that the books agree with the stock and bills. They run every night and on demand.", tid("run-integrity-checks-button")),
          go(GV + "system-health", "Backup and health", "Database connection, the latest migration and the background jobs."),
        ],
      },
    ],
  },
};

export const hasGuide = (moduleId) => Boolean(moduleId && GUIDES[moduleId]);

/** The module overview steps. When `role` has an intro for this module it is added as the second step. */
export function guideFor(moduleId, role) {
  const g = GUIDES[moduleId];
  if (!g) return [];
  const intro = role && g.roleIntro?.[role];
  if (!intro) return g.overview;
  const [first, ...rest] = g.overview;
  return [first, text(`Your part: ${ROLE_LABELS[role] || role}`, intro), ...rest];
}

/** Workflows of a module for this role: the ones meant for the role first. `inProject` hides project workflows when no project is open. */
export function workflowsFor(moduleId, role, inProject = true) {
  const list = (GUIDES[moduleId]?.workflows || []).filter((w) => inProject || !w.project);
  const mine = (w) => (w.roles || []).includes(role);
  return [...list.filter(mine), ...list.filter((w) => !mine(w))].map((w) => ({ ...w, forYou: mine(w) }));
}

export const workflowById = (moduleId, id) => (GUIDES[moduleId]?.workflows || []).find((w) => w.id === id);

/* ------------------------------------------------------------------------------------------------------------
   The whole job, start to finish: one guide that follows the business across every module, in the order the work
   happens. It is cut into stages so a user can start at any stage (and carry on to the end). Every stage opens with
   a short "what and who" box, then walks its pages like a workflow does. Steps inside a project use the project that
   is open, or the user's first project. */
export const STAGES = [
  {
    id: "set-up", short: "Set up", title: "Set up the company", who: ["ORG_ADMIN", "ACCOUNTS"],
    intro: "Do this once, before anyone raises a document. After it, forms fill in their own dropdowns and numbers.",
    steps: [
      go(OP + "companies", "Company and branches", "Your name, GSTIN, PAN, address and logo. They print on every bill and order. Add each office as a branch.", tid("create-company-button")),
      go(OP + "units-of-measure", "Units of measure", "Bags, sqm, cum, hours. Every quantity uses one of these.", tid("create-uom-button")),
      go(OP + "tax-codes", "Tax codes", "The GST rates used on orders and bills.", tid("create-tax-code-button")),
      go(OP + "bank-accounts", "Bank accounts", "Each bank account and cash box you pay from or receive into.", tid("create-bank-button")),
      go(OP + "numbering-series", "Document numbers", "How numbers such as PO/FY2627/0001 are built and when they restart.", tid("create-numbering-series-button")),
      go(OP + "approval-workflows", "Approval chains", "Who must approve each type of document, step by step. A step can name one person, or leave it to anyone with the role.", tid("create-workflow-button")),
      go(GV + "users", "Add your people", "Create a login for each person and give a role.", tid("add-user-button")),
      go(GV + "access", "What each role can do", "Pick a role or a person and tick what they can do in each module."),
    ],
  },
  {
    id: "win-work", short: "Win work", title: "Win the work", who: ["ORG_ADMIN", "PORTFOLIO_LEAD"],
    intro: "From the first enquiry to a signed contract. The accepted proposal becomes the project, with its activities and a draft estimate.",
    steps: [
      go(DL + "clients", "Add the client", "Create the client with its GSTIN and PAN. A wrong format is refused.", tid("create-client-button")),
      go(DL + "sales-flow", "Track the lead", "Leads move across the board from New to Won or Lost."),
      go(DL + "proposals", "Prepare the proposal", "List the work with quantity and rate. Your own cost per line stays internal and is never shown to the client."),
      go(DL + "sales-flow", "Contract and project", "When the client accepts, make the contract and create the project from it."),
    ],
  },
  {
    id: "plan", short: "Plan", title: "Plan the project", who: ["ORG_ADMIN", "PORTFOLIO_LEAD", "MEMBER"],
    intro: "Set the activities, the budget and the schedule before any money is spent. Progress and cost are measured against this plan. Steps inside a project use the project you have open, or your first one.",
    steps: [
      go(DL + "setup-library", "Templates", "Ready-made activities with units and a cost per unit. Use one when you create a project.", tid("create-template-button")),
      go(DL + "all-projects", "Create or open a project", "Every project you can see. Create a new one here, or open one to continue.", tid("create-project-button")),
      go(PJ + "wbs", "Activities and weights", "Check each activity's quantity. An activity with no quantity cannot show progress."),
      go(PJ + "estimates", "Estimate and budget", "Create an estimate with cost lines and approve it. The approved version is your budget.", tid("create-estimate-button")),
      go(PJ + "schedule", "Schedule", "Dates for each milestone.", tid("add-schedule-task-button")),
      go(PJ + "resources", "People and machines", "Plan the manpower and machines each activity needs.", tid("add-resource-button")),
    ],
  },
  {
    id: "buy", short: "Buy", title: "Buy material and give out work", who: ["PROCUREMENT", "MEMBER", "ORG_ADMIN"],
    intro: "Two routes. Material: indent, quotes, order, receipt. Subcontract work: requisition, work order, measurement.",
    steps: [
      go(OP + "vendors", "Vendors", "Add vendors with a valid GSTIN and PAN. A duplicate name is flagged.", tid("create-vendor-button")),
      go(OP + "purchase-indents", "Indent", "The site asks for material. Prices come from your Items and Prices list.", tid("create-indent-button")),
      go(OP + "rfqs", "Quotes (RFQ)", "Invite vendors, enter their quotes, select the best one and convert it to a purchase order.", tid("create-rfq-button")),
      go(OP + "purchase-orders", "Purchase order", "Submit for approval. Once approved it can be received.", tid("create-po-button")),
      go(OP + "goods-receipt-notes", "Receive the goods", "Record what arrived and post. You cannot receive more than was ordered.", tid("create-grn-button")),
      go(OP + "service-requisitions", "Subcontract: requisition", "Describe the work and pick the preferred vendor. Approving it drafts a work order.", tid("create-sr-button")),
      go(OP + "work-orders", "Subcontract: work order", "Check the rates and approve. A measurement (JMR) is drafted for each line.", tid("create-wo-button")),
      go(OP + "contractor-jmrs", "Subcontract: measure and certify", "Enter the measured quantity, up to 10% over the order, then certify.", tid("create-jmr-button")),
    ],
  },
  {
    id: "store", short: "Store", title: "Run the store", who: ["STORES"],
    intro: "Material received into a warehouse becomes stock. Issuing it to a job is when its cost reaches the project.",
    steps: [
      go(OP + "warehouses", "Warehouses", "Each site store or depot. Stock is tracked per item per warehouse.", tid("create-warehouse-button")),
      go(OP + "stock-and-balances", "Stock on hand", "What is in each store now, and its average cost."),
      go(OP + "goods-issues", "Issue to a job", "Pick the project and the activity. The cost moves from stock to the job.", tid("create-gin-button")),
      go(OP + "store-returns", "Returns from site", "Unused material coming back, at the cost it was issued at.", tid("create-srn-button")),
    ],
  },
  {
    id: "build", short: "Build", title: "Build on site", who: ["MEMBER", "ORG_ADMIN", "PORTFOLIO_LEAD"],
    intro: "Every day the site records what was done. This is what moves progress, cost and the schedule.",
    steps: [
      go(PJ + "construction", "Daily progress", "Enter today's output for each activity. Progress and the S-curve update from this."),
      go(PJ + "site-expenses", "Site expenses", "Diesel, food, transport and wages. One entry updates the project cost and the accounts.", tid("add-site-expense-button")),
      go(PJ + "logbook", "Equipment logbook", "Hours and fuel for each machine. This books the machine's cost to the activity.", tid("create-logbook-entry-button")),
      go(PJ + "daily-reports", "Daily report", "One report a day, put together from the day's entries. Submit it for approval.", tid("create-daily-report-button")),
      go(PJ + "issues", "Issues and risks", "Log anything blocking the work so it is followed to closure."),
    ],
  },
  {
    id: "people", short: "People", title: "Pay your people", who: ["HR", "ACCOUNTS"],
    intro: "Monthly: keep employees and attendance right, then run the payroll. Site staff salary is booked to the project.",
    steps: [
      go(PI + "employees", "Employees", "Salary details used by payroll.", tid("add-employee-button")),
      go(PI + "attendance", "Attendance", "Mark each day, or mark today for the whole team. Payroll uses this."),
      go(PI + "payroll-runs", "Payroll run", "Prepare the month, post it to the accounts, pay through the bank and record PF, ESI and TDS.", tid("new-payroll-run-button")),
    ],
  },
  {
    id: "money", short: "Bills & money", title: "Bills and money", who: ["ACCOUNTS", "ORG_ADMIN"],
    intro: "What you owe and what you are owed. Vendor bills are drafted for you from receipts and certified measurements.",
    steps: [
      go(OP + "purchase-bills", "Vendor bills", "Check each drafted bill, add the vendor's bill number and post.", tid("create-bill-button")),
      go(OP + "service-bills", "Subcontractor bills", "Post bills for certified work. Retention and TDS are taken off."),
      go(OP + "debit-notes", "Debit notes", "Reduce a bill for returns, price or quantity differences, quality or penalties.", tid("create-debit-note-button")),
      go(OP + "vendor-payments", "Pay vendors", "Allocate each payment to the bills it settles, oldest first.", tid("create-payment-button")),
      go(DL + "ra-bills", "Bill the client", "Bill the work done so far. You are warned if it is above certified progress.", tid("create-ra-bill-button")),
      go(DL + "client-payments", "Record receipts", "Enter the money received and allocate it to the bills it pays.", tid("create-client-payment-button")),
    ],
  },
  {
    id: "control", short: "Control & close", title: "Control and close", who: ["ORG_ADMIN", "PORTFOLIO_LEAD", "ACCOUNTS"],
    intro: "Check that cost and progress match the plan, review the numbers, close the month, and close the project when it is done.",
    steps: [
      go(PJ + "plan-vs-actual", "Plan vs actual", "Planned quantities against what was actually done."),
      go(PJ + "finance", "Project finance", "Budget, committed, actual cost, margin, cash and forecast in one place."),
      go(PI + "executive-dashboard", "Executive dashboard", "Revenue, cost and margin across all projects."),
      go(PI + "cost-to-completion", "Cost to completion", "For each activity: spent, committed and still to spend."),
      go(PI + "aging-reports", "Ageing", "Bills and receipts by how long they have been outstanding."),
      go(FA + "periods-and-year-end", "Close the month", "Closing a period blocks further posting into it. Do it only when the books are final."),
      go(PJ + "closeout", "Close the project", "Clear the checklist, move leftover stock to other sites, then close."),
    ],
  },
];

/** The whole journey, optionally starting at stage `fromId` and carrying on to the end. Each stage opens with a
 *  short box saying what it is and who usually does it. */
export function journeySteps(fromId) {
  const start = Math.max(0, STAGES.findIndex((s) => s.id === fromId));
  return STAGES.slice(start).flatMap((st, k) => {
    const n = start + k + 1;
    const who = st.who.map((r) => ROLE_LABELS[r] || r).join(", ");
    const landing = st.steps.find((s) => !s.to.includes(":projectId")) || st.steps[0];   // the box opens on a page that always exists
    return [
      { ...landing, el: undefined, title: `Stage ${n} of ${STAGES.length} · ${st.title}`, description: `${st.intro} Usually done by: ${who}.`, stage: st.id },
      ...st.steps.map((s) => ({ ...s, title: `${st.short} · ${s.title}`, stage: st.id })),
    ];
  });
}
